import { isEmpty, isEqual, filter, cloneDeep, debounce } from 'lodash'
import { useCallback, useEffect, useState, useRef, useMemo } from 'react'
import { Lock } from 'react-feather'
import { connect } from 'react-redux'
import { useMediaQuery } from 'react-responsive'
import { withRouter } from 'react-router'
import Sticky from 'react-sticky-el'
import { toast } from 'react-toastify'
import { Button, Col, Spinner } from 'reactstrap'
import { compose } from 'redux'
import { clearCart, dispatchSweetAlert, sendCartToGreenwing, fetchDiscounts, fetchCoupon } from '../../actions'
import { IS_PRODUCTION } from '../../constants'
import { createFetchDiscountsFormData, shouldFetchDiscounts } from '../../helpers/discountHelpers'
import { validateProceedToCheckout } from '../../redux/storeConfig/slices/slice.checkout'
import { fetchShippingRates } from '../../redux/storeConfig/slices/slice.checkoutRates'
import {
  createCheckoutSessionToken,
  updateCheckoutSessionToken,
  updateCheckoutSessionTokenMultiAddress
} from '../../redux/storeConfig/slices/slice.checkoutSessionToken'
import { isShipperHQActive } from '../../util/integrations'
import { cartItemsRequireCheckForApproval, returnCartItemsRequireApprovalError } from '../Cart/cartFunctions'
import ClearCartButton from '../Cart/ClearCartButton'
import {
  fetchCalculatedSubtotal,
  setCheckoutLoading,
  setSubtotalLoading,
  setErrors,
  setCustomCheckoutFee,
  resolveUnitPrice,
  setShippingQuote
} from '../Checkout/checkoutFunctions'
import { calculateTotalShippingCost } from '../Checkout/ShippingMethodSelector/shippingGroupsHelpers'
import dollar_price from '../general/DollarPrice'
import CheckoutConfirm from './CheckoutConfirm'
import { useUserIdentifier } from './hooks/useUserIdentifier'
import { buildShippingParams } from './services/shippingParamsBuilder'
import useRecalculateGrandTotal from './useRecalculateGrandTotal'
import {
  useCheckoutValidation,
  validateMinimumCreditCardTotal,
  getMinimumCreditCardValidationMessage,
  validateCheckoutForm,
  validateBillingAddress
} from './utils'

const OrderSummary = props => {
  const [showConfirmationModal, setShowConfirmationModal] = useState(false)
  const [checkoutErrorMessage, setCheckoutErrorMessage] = useState('')
  const [isZoomedIn, setIsZoomedIn] = useState(false)
  const [checkoutFormIsValid, setCheckoutFormIsValid] = useState(true)
  const [checkoutHasNoErrors, setCheckoutHasNoErrors] = useState(true)

  const punchoutFormRef = useRef(null)
  const { hasIdentifier } = useUserIdentifier()

  useRecalculateGrandTotal()

  // Use the checkout validation hook
  const {
    canProceed,
    allDigital: allDigitalFromHook,
    paymentGatewayType
  } = useCheckoutValidation({
    checkoutFormIsValid,
    checkoutHasNoErrors
  })

  // Determine the payment gateway type based on portal configuration
  const getPaymentGatewayType = () => {
    const gatewayType = props.portal?.payment_gateway?.type

    switch (gatewayType) {
      case 'stripe':
        return 'stripe'
      case 'authorize_net':
        return 'authorize_net'
      default:
        console.warn('⚠️ Unknown payment gateway type:', gatewayType, 'defaulting to authorize_net')
        return 'authorize_net'
    }
  }

  // Get complete payment gateway configuration
  const getPaymentGatewayConfig = () => {
    const gateway = props.portal?.payment_gateway
    const type = getPaymentGatewayType()
    const gatewayEnv = IS_PRODUCTION ? 'production' : 'sandbox'

    return {
      type,
      isStripe: type === 'stripe',
      isAuthorizeNet: type === 'authorize_net',
      config: gateway,
      clientKey: gateway?.client_key, // Changed from clientKey to client_key
      apiLoginID: gateway?.api_login_id, // Changed from apiLoginID to api_login_id
      publishableKey: gateway?.stripe_publishable_key, // Use stripe_publishable_key from portal config
      environment: gatewayEnv,
      currency: gateway?.supported_currencies?.[0]?.toLowerCase() || 'usd' // Get first supported currency, default to 'usd'
    }
  }

  const handlePunchout = useCallback(async () => {
    if (props.cartItems.length > 0) {
      const numberOfDiscountsApplied = filter(props.appliedPromotions, promo => promo.type === 'discount').length

      // Apply promotions to cart items like in checkout flow
      const cartForForm = props.cartItems.map(cartItem => {
        // Find if this item has any discounts from promotions
        const appliedPromotion = props.appliedPromotions[0]
        let discountedPrice = resolveUnitPrice(cartItem.priceData)

        if (appliedPromotion && appliedPromotion.updated_cart) {
          const updatedCartItem = appliedPromotion.updated_cart.find(
            updatedItem => updatedItem.cart_item_id === cartItem.cart_item_id
          )
          if (updatedCartItem && updatedCartItem.promo_data) {
            discountedPrice = updatedCartItem.promo_data.discounted_price
          }
        }

        return {
          ...cartItem,
          promo_data: {
            item_price: resolveUnitPrice(cartItem.priceData, false), // Original price without discounts
            discounted_price: discountedPrice,
            quantity: cartItem.quantity
          }
        }
      })

      const formDataDiscount = new FormData()
      formDataDiscount.append('applied_count', numberOfDiscountsApplied)
      formDataDiscount.append('subtotal', props.subtotal)
      formDataDiscount.append('shipping_cost', props.shippingQuote)
      formDataDiscount.append('cart_items', JSON.stringify(cartForForm))

      // Add promotion data if any promotions are applied
      if (props.appliedPromotions.length > 0) {
        const promotion = props.appliedPromotions[0]
        formDataDiscount.append('promotion_id', promotion.promotion_id)
        formDataDiscount.append('subtotal_reduction', props.checkout.subtotalReduction)
        formDataDiscount.append('shipping_reduction', props.checkout.shippingReduction)
        formDataDiscount.append('shipping_discount_in_cents', props.checkout.shippingReductionEncrypted)
        formDataDiscount.append('product_reduction', props.checkout.productReduction)
        formDataDiscount.append('updated_cart', JSON.stringify(promotion.updated_cart))
      }

      const response = await props.sendCartToGreenwing(formDataDiscount, props.currentLocation)

      if (!response) return // Error already handled by sendCartToGreenwing

      if (response?.cartdata && punchoutFormRef.current) {
        const cartdataInput = punchoutFormRef.current.querySelector('input[name="cartdata"]')
        if (!cartdataInput) {
          console.error('Cart data input not found')
          return
        }
        cartdataInput.value = response.cartdata
        punchoutFormRef.current.submit()

        await props.clearCart(props.currentLocation)
        toast.success('Your purchase request has been submitted')
        props.history.push('/')
      }
    }
  }, [
    props.cartItems,
    props.appliedPromotions,
    props.subtotal,
    props.shippingQuote,
    props.sendCartToGreenwing,
    props.currentLocation,
    props.checkout.subtotalReduction,
    props.checkout.shippingReduction,
    props.checkout.shippingReductionEncrypted,
    props.checkout.productReduction,
    props.clearCart,
    props.history,
    punchoutFormRef
  ])
  const { checkout, customerGroup, cartItems } = props

  const { selectedCustomerAddress, checkoutLoading, taxLoading, shippingLoading, creditsUsed, youPay, errors } =
    checkout

  // Check for multi-address errors (quantities not fully assigned to addresses)
  const hasMultiAddressErrors = Object.keys(errors).some(key => key.startsWith('multiAddress_') && errors[key] === true)

  // watch checkoutErrors and set message appropriately
  useEffect(() => {
    const checkoutErrorsExist =
      Object.values(errors).some(v => v === true || (typeof v === 'object' && !isEmpty(v))) ||
      Object.values(props.checkoutErrors).some(v => v === true)

    if (checkoutErrorsExist) {
      setCheckoutHasNoErrors(false)
      if (Object.keys(errors).some(key => key.includes('multiAddress') && errors[key] === true)) {
        setCheckoutErrorMessage('Please see multi-address selections.')
      } else if (Object.keys(errors).some(key => key.includes('requireApprovalRule') && errors[key] === true)) {
        setCheckoutErrorMessage('Cart cannot contains items that both require approval and do not require approval.')
      } else {
        setCheckoutErrorMessage('')
      }
    } else {
      setCheckoutErrorMessage('')
      setCheckoutHasNoErrors(true)
    }
  }, [errors])

  const cartItemsLength = useMemo(() => cartItems.length, [cartItems.length])

  // Use refs to track previous values for deep comparison
  const lastSubtotalParamsRef = useRef({
    cartItems: null,
    appliedPromotions: null
  })

  const debouncedFetchSubtotal = useMemo(
    () =>
      debounce((items, promotions) => {
        props.fetchCalculatedSubtotal(items, promotions)
      }, 200),
    [props.fetchCalculatedSubtotal]
  )

  // recalculate subtotal/setup charge if cartItems changes
  useEffect(() => {
    if (cartItemsLength > 0) {
      // Deep comparison to prevent unnecessary API calls
      const cartItemsChanged = !isEqual(cartItems, lastSubtotalParamsRef.current.cartItems)
      const appliedPromotionsChanged = !isEqual(
        props.appliedPromotions,
        lastSubtotalParamsRef.current.appliedPromotions
      )

      if (cartItemsChanged || appliedPromotionsChanged) {
        debouncedFetchSubtotal(cartItems, props.appliedPromotions)
        lastSubtotalParamsRef.current = {
          cartItems: cloneDeep(cartItems),
          appliedPromotions: cloneDeep(props.appliedPromotions)
        }
      }
    }

    // Only set maxQuantity error if user is guest AND there are actually max quantity restrictions
    if (!props.currentUser.id && cartItems.some(cartItem => cartItem.max_quantity)) {
      props.setErrors({ maxQuantity: true })
    } else {
      // Clear maxQuantity error for logged in users or when no max quantity restrictions
      props.setErrors({ maxQuantity: false })
    }
  }, [cartItemsLength, cartItems, props.appliedPromotions, debouncedFetchSubtotal])

  // Set custom checkout fee from location
  useEffect(() => {
    if (props.currentLocation) {
      props.setCustomCheckoutFee(props.currentLocation.custom_checkout_fee)
    }
  }, [props.currentLocation])

  // Check if cart items contain both requires approval and does not require approval
  useEffect(() => {
    if (cartItems && cartItems.length > 0) {
      const requiresApproval = cartItemsRequireCheckForApproval(null, cartItems, props.currentLocation)

      if (requiresApproval) {
        props.returnCartItemsRequireApprovalError(cartItems, null)
        props.setErrors({ requireApprovalRule: true })
      } else {
        props.setErrors({ requireApprovalRule: false })
      }
    } else {
      props.setErrors({ requireApprovalRule: false })
    }
  }, [cartItems])

  const detectZoom = () => {
    const zoomLevel = Math.round(window.devicePixelRatio * 100)
    if (zoomLevel > 100) {
      setIsZoomedIn(true)
    } else {
      setIsZoomedIn(false)
    }
  }

  useEffect(() => {
    window.addEventListener('resize', detectZoom)
    return () => window.removeEventListener('resize', detectZoom)
  }, [detectZoom])

  // validate checkout form
  useEffect(() => {
    const checkoutFormValid = validateCheckoutForm(props.checkoutFormData)
    setCheckoutFormIsValid(checkoutFormValid)
  }, [props.checkoutFormData])

  // Re-fetch promotions when inputs change - debounced to prevent multiple calls
  useEffect(() => {
    if (cartItemsLength > 0) {
      // Check if it's worth fetching discounts (skip if all prices are 0)
      const shouldFetch = shouldFetchDiscounts({
        subtotal: props.subtotal,
        shippingQuote: props.shippingQuote
      })

      if (!shouldFetch) {
        return
      }

      // Use setTimeout to debounce promotion fetching
      const timeoutId = setTimeout(() => {
        const formData = createFetchDiscountsFormData({
          cartItems: props.cartItems,
          subtotal: props.subtotal,
          shippingQuote: props.shippingQuote
        })
        props.fetchDiscounts(formData)

        if (props.checkout.appliedCoupon) {
          // Create separate form data for coupon to avoid mutation
          const couponFormData = createFetchDiscountsFormData({
            cartItems: props.cartItems,
            subtotal: props.subtotal,
            shippingQuote: props.shippingQuote
          })
          couponFormData.append('coupon_code', props.checkout.appliedCoupon)
          props.fetchCoupon(couponFormData, false)
        }
      }, 300) // 300ms debounce

      return () => clearTimeout(timeoutId)
    }
  }, [cartItemsLength, props.subtotal, props.shippingQuote])

  // Sync selectedRatesByGroup to checkout.shippingQuote for non-ShipperHQ flows
  useEffect(() => {
    const isShipperHQ = isShipperHQActive(props.integrations?.data)

    // Only update shipping quote for non-ShipperHQ flows
    if (!isShipperHQ && props.checkoutRates?.selectedRatesByGroup) {
      const totalShippingCost = calculateTotalShippingCost(props.checkoutRates.selectedRatesByGroup)

      // Only dispatch if the value has changed to avoid infinite loops
      if (totalShippingCost !== props.shippingQuote && totalShippingCost > 0) {
        props.setShippingQuote(totalShippingCost)
      }
    }
  }, [props.checkoutRates, props.integrations, props.shippingQuote, props.setShippingQuote])

  const proceedToCheckout = async () => {
    const userId = props.currentUser.id

    // if user is not logged in, show Toast error & redirect
    // only if guest checkout is not enabled for the portal
    if (!userId && props.portal.checkout_mode === 'registered_only') {
      toast.info('You need to Sign In to proceed to checkout!', {
        position: toast.POSITION.TOP_CENTER,
        type: toast.TYPE.SUCCESS,
        className: 'address-toast',
        autoClose: 3000
      })
      props.history.push(`/${props.portal.custom_login_url}`)
    } else if (errors.stock) {
      props.dispatchSweetAlert({
        type: 'error',
        alertMessage: 'Some items are out of stock! Please remove the highlighted items from your basket to continue.',
        showCancel: false,
        confirmBtnCssClass: 'mf-primary-btn alert-width-btn'
      })
    } else {
      // Sync checkout selections to backend before proceeding
      let token = props.checkoutSessionToken
      const multiAddressSelection = props.checkout.multiAddressSelection || []

      // Create token if it doesn't exist yet (cart page doesn't create tokens)
      if (!token) {
        const tokenResult = await props.createCheckoutSessionToken()
        token = tokenResult?.token
      }

      // Backend validation for multi-address checkout
      if (token && props.portal.multiple_address_shipping && userId) {
        const cartItemsForValidation = props.cartItems.map(item => ({
          cart_item_id: item.cart_item_id,
          quantity: item.quantity,
          shippable_type: item.shippable_type
        }))

        const { valid, errors: validationErrors } = await props.validateProceedToCheckout(token, {
          cartItems: cartItemsForValidation,
          multiAddressSelection
        })

        if (!valid) {
          const errorMessage = validationErrors?.length
            ? 'Please assign all item quantities to shipping addresses before proceeding.'
            : 'Checkout validation failed. Please try again.'

          props.dispatchSweetAlert({
            type: 'error',
            alertMessage: errorMessage,
            showCancel: false,
            confirmBtnCssClass: 'mf-primary-btn alert-width-btn'
          })
          return
        }
      }

      if (token) {
        const updateData = {
          useShippingAsBilling: props.checkout.useShippingAsBilling
        }

        // Save billing address (top-level field, available to all users)
        if (!isEmpty(props.checkout.selectedBillingAddress)) {
          updateData.selectedBillingAddress = props.checkout.selectedBillingAddress
        }

        // Save guest-specific data (only for guest users)
        if (!userId) {
          updateData.guestCheckoutData = {
            contact_email: props.checkout.contactEmail || null,
            selected_customer_address: props.checkout.selectedCustomerAddress || null,
            guest_addresses: props.checkout.guestAddresses || []
          }
        } else if (props.portal.multiple_address_shipping) {
          // Logged-in multi-address - save selections, clear single address
          if (!isEmpty(props.checkout.selectedBillingAddress)) {
            updateData.selectedBillingAddress = props.checkout.selectedBillingAddress
          }
          if (multiAddressSelection.length > 0) {
            updateData.multiAddressSelection = multiAddressSelection
          }

          updateData.selectedCustomerAddressId = null
        } else {
          // Logged-in single-address - save address ID, clear multi-address
          updateData.multiAddressSelection = []
          updateData.selectedCustomerAddressId = props.checkout.selectedCustomerAddress?.id
        }

        if (Object.keys(updateData).length > 0) {
          await props.updateCheckoutSessionToken(token, updateData)
        }
      }

      // Fetch shipping rates before proceeding to checkout (only for non-ShipperHQ)
      const isShipperHQ = isShipperHQActive(props.integrations?.data)
      const selectedAddress = props.checkout.selectedCustomerAddress

      if (!isShipperHQ && hasIdentifier && selectedAddress?.id && props.cartItems?.length > 0) {
        const shippableItems = props.cartItems.filter(item => item.shippable_type !== 'Digital Delivery')

        if (shippableItems.length > 0) {
          const multiAddressEnabled = props.portal.multiple_address_shipping && userId

          // For multi-address, check if all items have selections
          if (multiAddressEnabled) {
            const eachItemHasSelection = props.cartItems.every(cartItem =>
              props.checkout.multiAddressSelection.map(s => s.cartItemId).includes(cartItem.cart_item_id)
            )
            if (!eachItemHasSelection) {
              props.history.push('/check-out')
              return
            }
          }

          // Build params using centralized service
          const params = buildShippingParams({
            selectedAddress,
            cartItems: shippableItems,
            multiAddressSelection: props.checkout.multiAddressSelection,
            isMultiAddressEnabled: multiAddressEnabled
          })

          if (params) {
            await props.fetchShippingRates(params)
          }
        }
      }

      props.history.push('/check-out')
    }
  }

  // check for errors and show order confirmation modal
  const handlePlaceOrder = () => {
    // For non-ShipperHQ clients: validate that shipping rate is selected
    const isShipperHQ = isShipperHQActive(props.integrations?.data)
    if (!isShipperHQ && !allItemsDigitalDelivery) {
      const hasSelectedRates =
        props.checkoutRates?.selectedRatesByGroup && Object.keys(props.checkoutRates.selectedRatesByGroup).length > 0

      if (!hasSelectedRates) {
        props.dispatchSweetAlert({
          type: 'warning',
          alertMessage: 'Please select a shipping method before proceeding.',
          showCancel: false,
          confirmBtnCssClass: 'mf-primary-btn alert-width-btn mr-0'
        })
        return
      }
    }

    // Check minimum credit card validation first
    if (validateMinimumCreditCardTotal(youPay, checkout.paymentMethod)) {
      const validationMessage = getMinimumCreditCardValidationMessage()
      props.dispatchSweetAlert({
        type: 'warning',
        alertMessage: `${validationMessage.title}\n\n${validationMessage.message}`,
        showCancel: false,
        confirmBtnCssClass: 'mf-primary-btn alert-width-btn mr-0'
      })
      return
    }

    if (youPay > 0 && checkout.paymentMethod === 'credit_card') {
      // check CC details here
      const ccData = checkout.ccData
      const mm = ccData.card_expiry.substring(0, 2)
      const yy = ccData.card_expiry.substring(2)
      const now = new Date()
      const thisCentury = `${now.getFullYear()}`.substring(0, 2)
      const monthIndex = parseInt(mm) - 1
      const expiryDate = new Date(`${thisCentury}${yy}`, monthIndex)
      // remove spaces from card number
      ccData.card_number = ccData.card_number.replace(/\s/g, '')
      ////////////////////////////////
      // Disabled because we are not using the old credit card form, but the payment gateway
      // const ccError =
      //   expiryDate < now ||
      //   ccData.card_number.length < 12 ||
      //   ccData.card_number.length > 19 ||
      //   !ccData.card_name ||
      //   !ccData.card_cvc ||
      //   !ccData.card_expiry
      // if (ccError) {
      //   props.dispatchSweetAlert({
      //     type: 'error',
      //     alertMessage: 'Please check your card details.',
      //     showCancel: false,
      //     confirmBtnCssClass: 'mf-primary-btn alert-width-btn mr-0'
      //   })
      //   return
      // }
      ////////////////////////////////
    }
    setShowConfirmationModal(true)
  }

  const displayCreditsUsed = (label, amount) => {
    return (
      <Col lg="12" className="flex-space-between m-0 green-text">
        <h6>{label}:</h6>
        <h6>
          {checkout.checkoutLoading ||
          (checkout.shippingLoading && !isEmpty(selectedCustomerAddress)) ||
          checkout.taxLoading ? (
            <Spinner />
          ) : (
            <>-{dollar_price(amount)}</>
          )}
        </h6>
      </Col>
    )
  }

  const displayPriceReduction = (discountType, amount, showHorizontalRule = false) => {
    return (
      <>
        <Col lg="12" className="flex-space-between m-0 green-text">
          <h6>{discountType} Discount:</h6>
          <h6>-{dollar_price(amount)}</h6>
        </Col>
        {showHorizontalRule ? <hr className="w-100" /> : null}
      </>
    )
  }

  const isTabletOrMobile = useMediaQuery({ query: '(max-width: 992px)' })

  // CALCULATES HEIGHT OF SCROLL DOWN MENU AND ADDS MARGIN ON STICKY SIDEBAR
  const fixedHeaderElement = document.querySelector('.iron-fixed-header')
  const menuHeight = fixedHeaderElement !== null ? fixedHeaderElement.offsetHeight : '100px'
  const screenHeight = window.screen.availHeight
  const responseHeight = screenHeight !== null && screenHeight <= 770 ? true : false
  const allItemsDigitalDelivery = allDigitalFromHook
  const shippingAddressRequired = !allItemsDigitalDelivery
  const gatewayConfig = getPaymentGatewayConfig()
  const isGatewayPayment = gatewayConfig.isStripe || gatewayConfig.isAuthorizeNet

  const isBillingValid = useMemo(
    () =>
      validateBillingAddress({
        youPay: props.youPay,
        billingAddress: props.selectedBillingAddress,
        allDigital: allItemsDigitalDelivery,
        requireBillingAddressForFreeOrders: props.requireBillingAddressForFreeOrders
      }),
    [props.youPay, props.selectedBillingAddress, allItemsDigitalDelivery, props.requireBillingAddressForFreeOrders]
  )

  // Calculate shipping cost based on integration type
  const isShipperHQ = isShipperHQActive(props.integrations?.data)
  const displayShippingCost = isShipperHQ
    ? props.shippingQuote
    : calculateTotalShippingCost(props.checkoutRates?.selectedRatesByGroup)

  // For gateway payments, only critical loading states should block order placement
  // Address/shipping changes shouldn't block since payment processing is independent
  const criticalApisLoading = isGatewayPayment
    ? checkout.checkoutLoading || checkout.taxLoading || checkout.cart_loading
    : checkout.checkoutLoading || checkout.shippingLoading || checkout.taxLoading || checkout.cart_loading

  // Check minimum credit card validation - only apply when there's actually a credit card payment needed
  const minimumCreditCardValidationFailed = youPay > 0 && validateMinimumCreditCardTotal(youPay, checkout.paymentMethod)

  // For non-ShipperHQ clients: require shipping rate selection (isShipperHQ already defined above on line 600)
  const shippingRateRequired = !isShipperHQ && !allItemsDigitalDelivery
  const hasSelectedRates =
    props.checkoutRates?.selectedRatesByGroup && Object.keys(props.checkoutRates.selectedRatesByGroup).length > 0
  const shippingRateNotSelected = shippingRateRequired && !hasSelectedRates

  const placeYourOrderDisabled =
    !checkoutHasNoErrors ||
    !canProceed ||
    criticalApisLoading ||
    !isBillingValid ||
    minimumCreditCardValidationFailed ||
    shippingRateNotSelected

  const totalCostWithoutPaymentReduction = checkout.subtotal + checkout.taxQuote + checkout.shippingQuote

  return (
    <>
      <Sticky
        topOffset={0}
        stickyStyle={{
          top: menuHeight >= 90 ? '160px' : '100px',
          zIndex: '2'
        }}
        // onFixedToggle= {}
        disabled={isTabletOrMobile || responseHeight || isZoomedIn}
        className="m-auto order-summary-custom-css"
      >
        <div className="m-0 box-shadow white-background">
          <div className="ckout-header">
            <h5 className="m-0">Order Summary</h5>
          </div>
          {props.session?.punchout_buyer ? (
            <>
              <form ref={punchoutFormRef} method="POST" action={props.session?.return_url} style={{ display: 'none' }}>
                <input type="hidden" name="cartdata" />
              </form>

              <Col lg="12" className={props.currentLocation.show_price ? 'flex-space-between m-0 p-3' : 'd-none'}>
                {props.isCheckout ? (
                  <>
                    <h6>Subtotal:</h6>
                    <h6>{dollar_price(props.subtotal)}</h6>
                  </>
                ) : (
                  <>
                    <h5>Subtotal:</h5>
                    <h5>{dollar_price(props.subtotal)}</h5>
                  </>
                )}
                <Button className="w-100 mf-secure-co-btn mt-2 mb-2 ckout-btn-custom-css" onClick={handlePunchout}>
                  <Lock />
                  &nbsp;&nbsp;Submit Purchase Request
                </Button>
                <br />
                <ClearCartButton />
              </Col>
            </>
          ) : (
            <Col lg="12" className="p-3">
              <hr className={props.currentLocation.show_price ? 'w-100' : 'd-none'} />

              {/* Set Up Charge */}
              <Col lg="12" className={props.currentLocation.show_price ? 'flex-space-between m-0 mb-1' : 'd-none'}>
                {props.setupCharge > 0 ? (
                  props.isCheckout ? (
                    <>
                      <h6>Setup Charge:</h6>
                      <h6>{dollar_price(props.setupCharge)}</h6>
                    </>
                  ) : (
                    <>
                      <h5>Setup Charge:</h5>
                      <h5>{dollar_price(props.setupCharge)}</h5>
                    </>
                  )
                ) : null}
              </Col>

              {/* Sub Total */}
              <Col lg="12" className={props.currentLocation.show_price ? 'flex-space-between m-0' : 'd-none'}>
                {props.isCheckout ? (
                  <>
                    <h6>Subtotal:</h6>
                    <h6>{dollar_price(props.subtotal)}</h6>
                  </>
                ) : (
                  <>
                    <h5>Subtotal:</h5>
                    <h5>{dollar_price(props.subtotal)}</h5>
                  </>
                )}
              </Col>

              {/* Checkout Page Errors */}
              {props.isCheckout && (
                <>
                  {/* Checkout Form */}
                  {!checkoutFormIsValid && (
                    <>
                      <hr className="w-100" />
                      <Col lg="12" className="flex-space-between m-0">
                        <h6 className="red-text">Please Complete All Required Fields In Checkout Information Form</h6>
                      </Col>
                    </>
                  )}

                  {/* Guest Checkout Contact Email */}
                  {!props.currentUser.id && !props.checkout.contactEmail && (
                    <Col lg="12" className="flex-space-between m-0">
                      <h6
                        onClick={() => props.history.push('/cart')}
                        className="red-text"
                        style={{ cursor: 'pointer', textDecoration: 'underline' }}
                      >
                        Please enter a contact email.
                      </h6>
                    </Col>
                  )}

                  {/* Billing Address Required Fields */}
                  {!isBillingValid && (
                    <Col lg="12" className="m-0 mb-2">
                      <h6 className="red-text">Please complete all required fields in the Billing Address form.</h6>
                    </Col>
                  )}
                </>
              )}

              {/* Shipping */}
              {props.currentLocation.show_price && props.isCheckout ? (
                <>
                  <hr className="w-100" />
                  <Col lg="12" className="flex-space-between m-0">
                    {isEmpty(selectedCustomerAddress) && shippingAddressRequired ? (
                      <h6 className="red-text">
                        {props.currentUser.id
                          ? 'Please select a shipping address.'
                          : 'Please enter a shipping address.'}
                      </h6>
                    ) : (
                      <>
                        <h6>Shipping:</h6>
                        {props.shippingLoading === true ? <Spinner /> : <h6>{dollar_price(displayShippingCost)}</h6>}
                      </>
                    )}
                  </Col>
                </>
              ) : null}

              {props.checkout.shippingReduction ||
              props.checkout.subtotalReduction ||
              props.checkout.productReduction ? (
                <hr />
              ) : null}

              {/* Shipping Reduction from promotion */}
              {props.checkout.shippingReduction
                ? displayPriceReduction('Shipping', props.checkout.shippingReduction)
                : null}

              {/* Subtotal Reduction from promotion */}
              {props.checkout.subtotalReduction
                ? displayPriceReduction('Subtotal', props.checkout.subtotalReduction)
                : null}

              {/* Product Reduction from promotion */}
              {props.checkout.productReduction
                ? displayPriceReduction('Product', props.checkout.productReduction, true)
                : null}

              {/* Tax */}
              {props.currentLocation.show_price && props.isCheckout && !isEmpty(selectedCustomerAddress) ? (
                <>
                  <Col lg="12" className="flex-space-between m-0">
                    <h6>Sales Tax:</h6>
                    {props.checkout.taxLoading || props.checkout.checkoutLoading || props.checkout.shippingLoading ? (
                      <Spinner />
                    ) : (
                      <h6>{dollar_price(props.taxQuote)}</h6>
                    )}
                  </Col>
                </>
              ) : null}

              {/* Extra Checkout Fee */}
              {props.currentLocation.show_price && props.isCheckout && props.customCheckoutFee > 0 ? (
                <>
                  <Col lg="12" className="flex-space-between m-0">
                    <h6>{props.currentLocation.custom_checkout_fee_alias}:</h6>
                    <h6>{dollar_price(props.customCheckoutFee)}</h6>
                  </Col>
                </>
              ) : null}

              {/* Error For Not Enough LMS Points */}
              {props.portal.use_lms_points &&
              totalCostWithoutPaymentReduction > props.userCredits.lms_points.remaining ? (
                <Col lg="12" className="flex-space-between m-0">
                  <hr className="w-100" />
                  <h6 className="red-text">Not Enough Points To Checkout</h6>
                </Col>
              ) : null}

              {/* For Checkout Only */}
              {/* Co-Op & You Pay */}
              {props.isCheckout ? (
                <>
                  {/* Credits Used */}
                  {creditsUsed.coopCredits || creditsUsed.accountCredits || creditsUsed.giftCard ? <hr /> : null}

                  {/* Coop Reduction */}
                  {creditsUsed.coopCredits ? (
                    <Col lg="12" className="flex-space-between m-0 green-text">
                      <h6>{localStorage.getItem('coop_def')}:</h6>
                      <h6>-{dollar_price(creditsUsed.coopCredits)}</h6>
                    </Col>
                  ) : null}

                  {props.checkout.creditsUsed.accountCredits
                    ? displayCreditsUsed(localStorage.getItem('account_credit_def'), creditsUsed.accountCredits)
                    : null}

                  {props.checkout.creditsUsed.giftCard ? displayCreditsUsed('Gift Card', creditsUsed.giftCard) : null}

                  {props.checkout.creditsUsed.lmsPoints ? displayCreditsUsed('Points', creditsUsed.lmsPoints) : null}

                  {props.currentLocation.show_price ? (
                    <Col lg="12" className="flex-space-between m-0 mt-3">
                      <h5>You Pay:</h5>
                      <h5>
                        {props.checkout.checkoutLoading ||
                        (props.checkout.shippingLoading && !isEmpty(props.checkout.selectedCustomerAddress)) ||
                        props.checkout.taxLoading ? (
                          <Spinner />
                        ) : (
                          <>{dollar_price(props.youPay)}</>
                        )}
                      </h5>
                    </Col>
                  ) : null}

                  {/* Place Order Button (opens confirmation alert box) */}
                  <Col sm="12">
                    <Button
                      className="w-100 mf-secure-co-btn mt-3 mb-2 ml-0 mr-0 ckout-btn-custom-css"
                      onClick={handlePlaceOrder}
                      disabled={placeYourOrderDisabled}
                    >
                      <Lock />
                      &nbsp;&nbsp;Place Your Order
                    </Button>
                    {checkoutErrorMessage ? <p style={{ color: 'red' }}>{checkoutErrorMessage}</p> : null}
                  </Col>
                </>
              ) : null}

              {!props.isCheckout && (
                <>
                  <Button
                    className="w-100 mf-secure-co-btn mt-2 mb-2 ckout-btn-custom-css"
                    onClick={proceedToCheckout}
                    disabled={
                      !!props.errorMessage ||
                      props.checkout.maxQuantityLoading ||
                      props.checkout.checkoutLoading ||
                      props.checkout.cart_loading ||
                      props.checkoutRates?.status === 'loading' ||
                      cartItemsRequireCheckForApproval(null, props.cartItems, props.currentLocation) ||
                      (shippingAddressRequired && isEmpty(selectedCustomerAddress)) ||
                      (!props.currentUser.id && props.checkoutErrors.contactEmail) ||
                      hasMultiAddressErrors ||
                      props.validationStatus === 'loading'
                    }
                  >
                    {props.validationStatus === 'loading' ? (
                      <>
                        <Spinner size="sm" />
                        &nbsp;&nbsp;Validating...
                      </>
                    ) : props.checkoutRates?.status === 'loading' ? (
                      <>
                        <Spinner size="sm" />
                        &nbsp;&nbsp;Loading Shipping Rates...
                      </>
                    ) : (
                      <>
                        <Lock />
                        &nbsp;&nbsp;Proceed to Checkout
                      </>
                    )}
                  </Button>
                  {hasMultiAddressErrors && <p style={{ color: 'red' }}>{checkoutErrorMessage}</p>}
                  <br />
                  <sub>{props.errorMessage}</sub>
                  <ClearCartButton />
                </>
              )}
            </Col>
          )}
        </div>
      </Sticky>
      {/* Order Confirmation Modal */}
      <CheckoutConfirm
        canProceed={canProceed}
        show={showConfirmationModal}
        paymentMethod={checkout.paymentMethod}
        // net_30_payments={customerGroup?.net_30_payments || false}
        hideModal={() => setShowConfirmationModal(false)}
        paymentGatewayType={getPaymentGatewayType()}
        paymentGatewayConfig={getPaymentGatewayConfig()}
      />
    </>
  )
}

const mapStateToProps = state => {
  return {
    cartItems: state.cartItems,
    appliedPromotions: state.checkout.appliedPromotions,
    grandTotal: state.checkout.grandTotal,
    subtotal: state.checkout.subtotal,
    portal: state.portal,
    errorMessage: state.checkout.errorMessage,
    taxQuote: state.checkout.taxQuote,
    shippingQuote: state.checkout.shippingQuote,
    errorsOnScreen: state.errors,
    checkout: state.checkout,
    selectedBillingAddress: state.checkout.selectedBillingAddress,
    reduxErrors: state.errors,
    shippingLoading: state.checkout.shippingLoading,
    currentLocation: state.storeViews.current,
    userCredits: state.userCredits,
    setupCharge: state.checkout.setupCharge,
    currentUser: state.currentUser,
    customCheckoutFee: state.checkout.customCheckoutFee,
    checkoutForm: state.checkoutForm,
    checkoutFormData: state.checkout.checkoutFormData,
    customerGroup: state.customerGroup,
    requireBillingAddressForFreeOrders: state.storeViews.current.require_billing_address_for_free_orders,
    session: state.session,
    youPay: state.checkout.youPay,
    checkoutErrors: state.checkout.errors,
    checkoutSessionShortCode: state.checkoutSessionToken.shortCode,
    checkoutSessionToken: state.checkoutSessionToken.token,
    integrations: state.integrations,
    checkoutRates: state.checkoutRates,
    validationStatus: state.checkoutValidation?.validationStatus || 'init'
  }
}

const mapDispatchToProps = {
  clearCart,
  dispatchSweetAlert,
  sendCartToGreenwing,
  fetchDiscounts,
  fetchCoupon,
  fetchCalculatedSubtotal,
  setCheckoutLoading,
  setSubtotalLoading,
  returnCartItemsRequireApprovalError,
  setErrors,
  setCustomCheckoutFee,
  createCheckoutSessionToken,
  updateCheckoutSessionToken,
  updateCheckoutSessionTokenMultiAddress,
  validateProceedToCheckout,
  fetchShippingRates,
  setShippingQuote
}

export default compose(withRouter, connect(mapStateToProps, mapDispatchToProps))(OrderSummary)
